import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { notification } from 'antd'
import { annotationRepository } from 'domain/annotation'
import useAuth from 'hooks/useAuth'
import useBuildingUsersAndGroups from 'hooks/useBuildingUsersAndGroups'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import {
  buildingModeState,
  listPointState,
  SelectedMode,
  selectedPointDetail
} from 'store/buildingStore'
import { displayToggleState } from 'store/displayToggleStore'
import currentViewerState from 'store/viewerStore'
import useSWR, { useSWRConfig } from 'swr'
import { Annotation, AnnotationType } from 'types/building'
import Krpano from 'utils/krpano'
import {
  getPointVersion,
  getPointVersionFromResponse,
  handlePointLockedError,
  handlePointVersionError,
  syncPointVersion
} from 'utils/pointVersion'

import { CommentDomainService } from './services/CommentDomainService'
import { CommentMapper } from './mappers'
import { commentRepository } from './repository'
import {
  AddReplyDto,
  CommentItem,
  CommentReply,
  CreateCommentDto,
  FetchCommentsFilterParams,
  FetchCommentsResponse,
  MentionType,
  UpdateCommentDto,
  UpdateCommentStatusDto
} from './types'

export const COMMENT_CACHE_KEYS = {
  list: (options?: FetchCommentsFilterParams | null) =>
    options?.buildingId && options?.pointId && options.pointId !== 'all'
      ? ['comments', options]
      : null,
  detail: (commentId?: string | number | null) =>
    commentId ? ['comment', commentId] : null
}

export function useFetchComments(
  buildingIdOrOptions?: string | number | FetchCommentsFilterParams,
  floorId?: string | number,
  pointId?: string,
  status?: string,
  page?: number,
  perPage?: number,
  subImageId?: number | string
) {
  const options: FetchCommentsFilterParams | null = useMemo(() => {
    if (!buildingIdOrOptions) return null
    let bId: number | string
    let fId: number | string | undefined
    let pId: string | undefined
    let sImgId: number | string | undefined
    let st: string | undefined
    let pg: number | undefined
    let pPg: number | undefined

    if (typeof buildingIdOrOptions === 'object') {
      bId = buildingIdOrOptions.buildingId
      fId = buildingIdOrOptions.floorId
      pId = buildingIdOrOptions.pointId
      sImgId =
        buildingIdOrOptions.subImageId ?? buildingIdOrOptions.sub_image_id
      st = buildingIdOrOptions.status
      pg = buildingIdOrOptions.page
      pPg = buildingIdOrOptions.perPage
    } else {
      bId = buildingIdOrOptions
      fId = floorId
      pId = pointId
      st = status
      pg = page
      pPg = perPage
      sImgId = subImageId
    }

    if (pId === 'all') pId = undefined
    if (st === 'all') st = undefined
    if (sImgId === 'all') sImgId = undefined

    const cleaned: FetchCommentsFilterParams = { buildingId: String(bId) }
    if (fId) cleaned.floorId = fId
    if (pId) cleaned.pointId = pId
    if (sImgId !== undefined && sImgId !== null && sImgId !== '') {
      cleaned.subImageId = sImgId
    }
    if (st) cleaned.status = st
    if (pg && pg > 1) cleaned.page = pg
    if (pPg) cleaned.perPage = pPg

    return cleaned
  }, [buildingIdOrOptions, floorId, pointId, status, page, perPage, subImageId])

  const cacheKey = COMMENT_CACHE_KEYS.list(options)

  const { data, error, mutate, isLoading } = useSWR<FetchCommentsResponse>(
    cacheKey,
    () => commentRepository.getComments(options!),
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
      dedupingInterval: 60000
    }
  )

  const bId = options?.buildingId
  const { userList } = useBuildingUsersAndGroups(bId)

  const comments = useMemo(() => {
    const rawList = data?.data || []
    if (!rawList.length || !userList?.length) return rawList
    return rawList.map((item) =>
      CommentDomainService.mergeAdminRecipientsToComment(item, userList)
    )
  }, [data?.data, userList])

  return {
    comments,
    total: data?.total ?? 0,
    unfilteredTotal: data?.unfilteredTotal ?? 0,
    unresolvedCount: data?.unresolvedCount ?? 0,
    page: data?.page ?? 1,
    perPage: data?.perPage ?? 20,
    totalPages: data?.totalPages ?? 1,
    isLoading,
    error,
    mutate
  }
}

export function useFetchCommentDetail(commentId?: number | string | null) {
  const { data, error, mutate, isLoading } = useSWR<CommentItem>(
    COMMENT_CACHE_KEYS.detail(commentId),
    () => commentRepository.getCommentDetail(commentId!)
  )

  return {
    comment: data,
    isLoading,
    error,
    mutate
  }
}

export function useMutateComments() {
  const { mutate } = useSWRConfig()
  return () => {
    mutate(
      (key: any) => Array.isArray(key) && key[0] === 'comments',
      undefined,
      { revalidate: true }
    )
  }
}

export function useCommentActions() {
  const { t } = useTranslation()
  const mutateComments = useMutateComments()

  const createComment = async (payload: CreateCommentDto) => {
    try {
      const res = await commentRepository.createComment(payload)
      mutateComments()
      return res
    } catch (err) {
      handlePointLockedError(err, t)
      throw err
    }
  }

  const updateComment = async (
    commentId: number | string,
    payload: UpdateCommentDto
  ) => {
    try {
      const res = await commentRepository.updateComment(commentId, payload)
      mutateComments()
      return res
    } catch (err) {
      handlePointLockedError(err, t)
      throw err
    }
  }

  const updateCommentStatus = async (payload: UpdateCommentStatusDto) => {
    try {
      const res = await commentRepository.updateStatus(payload)
      mutateComments()
      return res
    } catch (err) {
      handlePointLockedError(err, t)
      throw err
    }
  }

  const addReply = async (
    commentId: number | string,
    bodyOrPayload: AddReplyDto | string
  ) => {
    const res = await commentRepository.addReply(commentId, bodyOrPayload)
    mutateComments()
    return res
  }

  const updateReply = async (
    replyId: number | string,
    bodyOrPayload: AddReplyDto | string
  ) => {
    const res = await commentRepository.updateReply(replyId, bodyOrPayload)
    mutateComments()
    return res
  }

  const deleteReply = async (replyId: number | string) => {
    const res = await commentRepository.deleteReply(replyId)
    mutateComments()
    return res
  }

  const deleteComment = async (commentId: number | string) => {
    const res = await commentRepository.deleteComment(commentId)
    mutateComments()
    return res
  }

  return {
    createComment,
    updateComment,
    updateCommentStatus,
    addReply,
    updateReply,
    deleteReply,
    deleteComment,
    mutateComments
  }
}

export function useFetchReplies(commentId?: number | string, isOpen?: boolean) {
  const [replies, setReplies] = useState<CommentReply[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const fetchPage = useCallback(
    async (targetPage: number, append: boolean = false) => {
      if (!commentId) return
      if (append) {
        setIsLoadingMore(true)
      } else {
        setIsLoading(true)
      }

      try {
        const res = await commentRepository.getReplies(
          commentId,
          targetPage,
          10
        )
        setTotalPages(res.totalPages)
        setTotal(res.total)
        setPage(targetPage)

        setReplies((prev) => {
          if (append) {
            const existingIds = new Set(prev.map((r) => String(r.id)))
            const newItems = (res.data || []).filter(
              (r) => !existingIds.has(String(r.id))
            )
            return [...prev, ...newItems]
          }
          return res.data || []
        })
      } catch {
        // Handle error silently
      } finally {
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    [commentId]
  )

  useEffect(() => {
    if (isOpen && commentId) {
      setReplies([])
      setPage(1)
      setTotalPages(1)
      setTotal(0)
      fetchPage(1, false)
    }
  }, [isOpen, commentId, fetchPage])

  const loadMoreOlder = useCallback(async () => {
    if (isLoadingMore || isLoading || page >= totalPages) return
    const nextPage = page + 1
    await fetchPage(nextPage, true)
  }, [isLoadingMore, isLoading, page, totalPages, fetchPage])

  const refetchLoadedPages = useCallback(async () => {
    if (!commentId) return
    setIsLoading(true)
    try {
      const fetchPromises = []
      for (let p = 1; p <= page; p += 1) {
        fetchPromises.push(commentRepository.getReplies(commentId, p, 10))
      }
      const results = await Promise.all(fetchPromises)
      const allReplies: CommentReply[] = []
      const seenIds = new Set<string>()

      results.forEach((res) => {
        if (res.data && Array.isArray(res.data)) {
          res.data.forEach((item: CommentReply) => {
            const key = String(item.id)
            if (!seenIds.has(key)) {
              seenIds.add(key)
              allReplies.push(item)
            }
          })
        }
      })

      if (results[0]) {
        setTotalPages(results[0].totalPages)
        setTotal(results[0].total)
      }
      setReplies(allReplies)
    } catch {
      // Handle error silently
    } finally {
      setIsLoading(false)
    }
  }, [commentId, page])

  return {
    replies,
    page,
    totalPages,
    total,
    isLoading,
    isLoadingMore,
    hasMore: page < totalPages,
    hasMoreOlder: page < totalPages,
    loadMore: loadMoreOlder,
    loadMoreOlder,
    refetchLoadedPages,
    reload: () => fetchPage(1, false)
  }
}

export interface SaveCommentWorkflowParams {
  buildingId: number | string
  floorId: number | string
  pointId: string
  currentSelected: {
    h?: number
    v?: number
    head_h?: number
    head_v?: number
    mid_h?: number
    mid_v?: number
    id?: string | number
    comment_id?: number
    [key: string]: any
  }
  payload: {
    title: string
    objectCat: string
    scope: string
    status: string
    body: string
    s3Path?: string
    subImageId?: number | string | null
    sub_image_id?: number | string | null
    recipients?: number[]
    year?: string
    mentions?: {
      mention_type: MentionType
      target_id?: number
    }[]
  }
  pointDetail?: any
  points?: any[]
}

export function useCommentWorkflow() {
  const { t } = useTranslation()
  const { mutateComments } = useCommentActions()
  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const [points, setPoints] = useRecoilState(listPointState)

  const saveCommentWithAnnotation = async ({
    buildingId,
    floorId,
    pointId,
    currentSelected,
    payload,
    pointDetail: passedPointDetail,
    points: passedPoints
  }: SaveCommentWorkflowParams): Promise<{
    annotation: Annotation
    comment?: CommentItem
    isNew: boolean
    pointVersionId?: number
  }> => {
    const isNew = !currentSelected?.id
    const activePointDetail = passedPointDetail || pointDetail
    const activePoints = passedPoints || points
    const versionIdToSend = getPointVersion(
      pointId,
      activePointDetail,
      activePoints
    )

    const rawSubImg = payload.subImageId ?? payload.sub_image_id
    let parsedSubImageId: number | null | undefined
    if (
      rawSubImg !== undefined &&
      rawSubImg !== null &&
      String(rawSubImg) !== ''
    ) {
      parsedSubImageId = Number(rawSubImg)
    } else if (rawSubImg === null) {
      parsedSubImageId = null
    }

    if (isNew) {
      // Step 1: Create Annotation of type COMMENT in DynamoDB
      let annotationData: Annotation
      try {
        const res = await annotationRepository.saveAnnotation(
          {
            building_id: Number(buildingId),
            floor_id: Number(floorId),
            point_id: pointId,
            annotation: {
              h: currentSelected?.h,
              v: currentSelected?.v,
              head_h: currentSelected?.head_h,
              head_v: currentSelected?.head_v,
              mid_h: currentSelected?.mid_h,
              mid_v: currentSelected?.mid_v,
              imageID: parsedSubImageId ?? undefined,
              year: payload.year?.trim() || undefined,
              type: AnnotationType.COMMENT
            }
          },
          versionIdToSend
        )
        annotationData = res.data
      } catch (err) {
        handlePointVersionError(err, t)
        handlePointLockedError(err, t)
        throw err
      }

      const step1Version =
        getPointVersionFromResponse(annotationData) ?? versionIdToSend

      // Step 2: Create Comment in PostgreSQL with rollback on failure
      let commentData: CommentItem
      try {
        commentData = await commentRepository.createComment(
          CommentMapper.toCreateDto({
            buildingId: Number(buildingId),
            floorId: Number(floorId),
            pointId,
            subImageId: parsedSubImageId,
            annotationId: String(annotationData.id),
            title: payload.title,
            body: payload.body,
            s3Path: payload.s3Path,
            objectCat: payload.objectCat,
            scope: payload.scope,
            status: payload.status,
            posH: currentSelected?.h ?? 0,
            posV: currentSelected?.v ?? 0,
            recipients: payload.recipients,
            mentions: payload.mentions
          })
        )
      } catch (err) {
        // Rollback DynamoDB annotation to prevent phantom markers
        if (annotationData?.id) {
          try {
            await annotationRepository.deleteAnnotation({
              annotation_id: annotationData.id,
              building_id: Number(buildingId),
              floor_id: Number(floorId),
              point_id: pointId
            })
          } catch {
            // Ignore rollback deletion error
          }
        }
        handlePointLockedError(err, t)
        throw err
      }

      // Step 3: Link PostgreSQL Comment ID to DynamoDB Annotation
      const updateAnnotationPayload: Annotation = {
        ...annotationData,
        id: annotationData.id,
        imageID: parsedSubImageId ?? annotationData?.imageID ?? undefined,
        h: currentSelected?.h,
        v: currentSelected?.v,
        head_h: currentSelected?.head_h ?? annotationData?.head_h,
        head_v: currentSelected?.head_v ?? annotationData?.head_v,
        mid_h: currentSelected?.mid_h ?? annotationData?.mid_h,
        mid_v: currentSelected?.mid_v ?? annotationData?.mid_v,
        year: payload.year?.trim() || undefined,
        type: AnnotationType.COMMENT,
        comment_object: {
          id: commentData.id,
          comment_id: commentData.id,
          object_cat: payload.objectCat,
          scope: payload.scope,
          status: payload.status,
          year: payload.year?.trim() || undefined,
          recipients: payload.recipients
        }
      }

      let updatedAnnotationData: Annotation | undefined
      try {
        const res = await annotationRepository.saveAnnotation(
          {
            building_id: Number(buildingId),
            floor_id: Number(floorId),
            point_id: pointId,
            annotation: updateAnnotationPayload
          },
          step1Version
        )
        updatedAnnotationData = res.data
      } catch (err) {
        handlePointVersionError(err, t)
        handlePointLockedError(err, t)
        throw err
      }

      const finalAnnotation = updatedAnnotationData || updateAnnotationPayload
      const newVersion =
        getPointVersionFromResponse(finalAnnotation) ?? step1Version

      syncPointVersion(newVersion, pointId, setPointDetail, setPoints)
      mutateComments()
      return {
        annotation: finalAnnotation,
        comment: commentData,
        isNew: true,
        pointVersionId: newVersion
      }
    }

    // Updating existing comment
    const commentId =
      currentSelected?.comment_object?.id ||
      currentSelected?.comment_object?.comment_id ||
      currentSelected?.comment_id

    const rawUpdateSubImg = payload.subImageId ?? payload.sub_image_id
    let parsedUpdateSubImageId: number | null | undefined
    if (
      rawUpdateSubImg !== undefined &&
      rawUpdateSubImg !== null &&
      String(rawUpdateSubImg) !== ''
    ) {
      parsedUpdateSubImageId = Number(rawUpdateSubImg)
    } else if (rawUpdateSubImg === null) {
      parsedUpdateSubImageId = null
    }

    const prevCommentObj =
      currentSelected?.comment_object ||
      currentSelected?.commentObject ||
      currentSelected?.comment
    const prevRecipients: number[] | undefined = (
      prevCommentObj?.recipients || currentSelected?.recipients
    )?.map((r: any) =>
      typeof r === 'number'
        ? r
        : r?.recipientId ?? r?.recipient_id ?? r?.id ?? r
    )

    const prevDto = CommentMapper.toUpdateDto({
      title:
        prevCommentObj?.title ||
        currentSelected?.name ||
        currentSelected?.title ||
        '',
      body: prevCommentObj?.body || currentSelected?.body || '',
      s3Path:
        prevCommentObj?.s3_path ||
        prevCommentObj?.s3Path ||
        currentSelected?.s3_path ||
        currentSelected?.s3Path,
      objectCat:
        prevCommentObj?.object_cat ||
        prevCommentObj?.objectCat ||
        currentSelected?.objectCat ||
        'A',
      scope: prevCommentObj?.scope || currentSelected?.scope || 'common',
      status: prevCommentObj?.status || currentSelected?.status || 'open',
      subImageId:
        currentSelected?.imageID != null
          ? Number(currentSelected.imageID)
          : undefined,
      recipients: prevRecipients
    })

    let commentData: CommentItem | undefined
    if (commentId) {
      try {
        commentData = await commentRepository.updateComment(
          commentId,
          CommentMapper.toUpdateDto({
            title: payload.title,
            body: payload.body,
            s3Path: payload.s3Path,
            objectCat: payload.objectCat,
            scope: payload.scope,
            status: payload.status,
            subImageId: parsedUpdateSubImageId,
            recipients: payload.recipients,
            mentions: payload.mentions
          })
        )
      } catch (err) {
        handlePointLockedError(err, t)
        throw err
      }
    }

    const annotationPayload: Annotation = {
      ...currentSelected,
      id: currentSelected.id ? String(currentSelected.id) : undefined,
      imageID: parsedUpdateSubImageId ?? currentSelected?.imageID ?? undefined,
      year: payload.year?.trim() || undefined,
      type: AnnotationType.COMMENT,
      comment_object: {
        ...(currentSelected?.comment_object || {}),
        comment_id: commentId,
        id: commentId,
        object_cat: payload.objectCat,
        scope: payload.scope,
        status: payload.status,
        year: payload.year?.trim() || undefined
      }
    }

    let updatedAnnotation: Annotation | undefined
    try {
      const res = await annotationRepository.saveAnnotation(
        {
          building_id: Number(buildingId),
          floor_id: Number(floorId),
          point_id: pointId,
          annotation: annotationPayload
        },
        versionIdToSend
      )
      updatedAnnotation = res.data
    } catch (err) {
      // Rollback PostgreSQL comment to previous state on annotation save failure
      if (commentId) {
        try {
          await commentRepository.updateComment(commentId, prevDto)
        } catch {
          // Ignore rollback error
        }
      }
      handlePointVersionError(err, t)
      handlePointLockedError(err, t)
      throw err
    }

    const finalAnnotation = updatedAnnotation || annotationPayload
    const newVersion =
      getPointVersionFromResponse(finalAnnotation) ?? versionIdToSend

    syncPointVersion(newVersion, pointId, setPointDetail, setPoints)
    mutateComments()
    return {
      annotation: finalAnnotation,
      comment: commentData,
      isNew: false,
      pointVersionId: newVersion
    }
  }

  return {
    saveCommentWithAnnotation
  }
}

export function useRNViewerLock() {
  const { id: paramBuildingId, floorId: paramFloorId } = useParams()
  const [searchParams] = useSearchParams()
  const { profile } = useAuth()
  const { t } = useTranslation()
  const { isRN } = useDirectory()

  const currentViewer = useRecoilValue(currentViewerState)
  const setCurrentViewer = useSetRecoilState(currentViewerState)
  const pointDetailRecoil = useRecoilValue(selectedPointDetail)
  const setPointDetail = useSetRecoilState(selectedPointDetail)
  const points = useRecoilValue(listPointState)
  const setPoints = useSetRecoilState(listPointState)
  const setDisplayToggle = useSetRecoilState(displayToggleState)
  const [buildingMode, setBuildingMode] = useRecoilState(buildingModeState)

  const { trigger: handleLockUnLock, isMutating: isLocking } =
    useMutation('v1/points/lock')

  const activePointDetail = currentViewer?.pointDetail || pointDetailRecoil

  useEffect(() => {
    setDisplayToggle({ isLocked: false, lockedBy: '' })
  }, [currentViewer?.selectedPoint, setDisplayToggle])

  const isCheckLockedPoint = useMemo(() => {
    const isLocked = Boolean(activePointDetail?.isLocked)
    const lockedBy = activePointDetail?.lockedBy
    return Boolean(
      isLocked && profile?.id && String(profile.id) === String(lockedBy)
    )
  }, [activePointDetail?.isLocked, activePointDetail?.lockedBy, profile?.id])

  const isLockedByAnother = useMemo(() => {
    const isLocked = Boolean(activePointDetail?.isLocked)
    const lockedBy = activePointDetail?.lockedBy
    return Boolean(
      isLocked &&
        lockedBy &&
        profile?.id &&
        String(profile.id) !== String(lockedBy)
    )
  }, [activePointDetail?.isLocked, activePointDetail?.lockedBy, profile?.id])

  useEffect(() => {
    if (!isRN) return
    if (isCheckLockedPoint) {
      setBuildingMode((prev) => ({
        ...prev,
        selectedMode: SelectedMode.RN_COMMENT_ANNOTATION
      }))
    } else {
      setBuildingMode((prev) => ({
        ...prev,
        selectedMode:
          prev.selectedMode === SelectedMode.RN_COMMENT_ANNOTATION
            ? SelectedMode.NONE
            : prev.selectedMode
      }))
    }
  }, [isCheckLockedPoint, isRN, setBuildingMode])

  const toggleCommentLock = useCallback(() => {
    if (isLockedByAnother || isLocking || !profile?.id) return

    notification.destroy('placingAnnotation')
    notification.destroy('placingSetAnnotation')

    const activePointId =
      currentViewer?.selectedPoint ||
      (activePointDetail?.id ? String(activePointDetail.id) : undefined) ||
      searchParams.get('point') ||
      ''
    const activeFloorId =
      paramFloorId ||
      (currentViewer?.selectedFloor
        ? String(currentViewer.selectedFloor)
        : undefined) ||
      searchParams.get('floor') ||
      ''
    const activeBuildingId =
      paramBuildingId ||
      ((activePointDetail as any)?.buildingId
        ? String((activePointDetail as any).buildingId)
        : undefined) ||
      ((activePointDetail as any)?.image360?.buildingId
        ? String((activePointDetail as any).image360.buildingId)
        : undefined) ||
      searchParams.get('building') ||
      ''

    if (!activePointId) return

    const versionIdToSend = getPointVersion(
      activePointId,
      activePointDetail,
      points
    )

    handleLockUnLock(
      {
        headers:
          versionIdToSend != null
            ? { 'X-Point-Version-ID': String(versionIdToSend) }
            : {},
        data: {
          building_id: Number(activeBuildingId),
          floor_id: Number(activeFloorId)
        },
        query: [activePointId]
      },
      {
        onSuccess: (data) => {
          const { isLocked, lockedBy } = data.data
          const newVersion =
            getPointVersionFromResponse(data.data) ?? data.data.pointVersionId
          const numericProfileId = profile?.id ? Number(profile.id) : null

          Krpano.TogglePointLock(isLocked)
          setDisplayToggle({
            lockedBy: isLocked ? profile?.id || '' : '',
            isLocked
          })
          setCurrentViewer((prev) => {
            if (!prev?.pointDetail) return prev
            return {
              ...prev,
              pointDetail: {
                ...prev.pointDetail,
                isLocked,
                lockedBy: isLocked ? numericProfileId : null,
                lockedByUser: isLocked ? lockedBy : null,
                ...(newVersion != null ? { pointVersionId: newVersion } : {})
              }
            }
          })
          syncPointVersion(newVersion, activePointId, setPointDetail, setPoints)
          if (!isLocked) {
            setBuildingMode((prev) => ({
              ...prev,
              selectedMode: SelectedMode.NONE
            }))
          }
        },
        onError: (error) => {
          if (handlePointVersionError(error, t)) return
          handlePointLockedError(error, t)
        }
      }
    )
  }, [
    isLockedByAnother,
    isLocking,
    profile?.id,
    currentViewer?.selectedPoint,
    currentViewer?.selectedFloor,
    searchParams,
    paramFloorId,
    paramBuildingId,
    activePointDetail,
    handleLockUnLock,
    setDisplayToggle,
    setCurrentViewer,
    setBuildingMode,
    t
  ])

  const isCommentModeActive =
    buildingMode.selectedMode === SelectedMode.RN_COMMENT_ANNOTATION

  const buttonClassName = useMemo(() => {
    if (isLockedByAnother) {
      return 'bg-[#02112066] text-slate-400 cursor-not-allowed'
    }
    if (isCommentModeActive || isCheckLockedPoint) {
      return 'theme-bg-sub-primary text-white cursor-pointer'
    }
    return 'bg-[#021120CC] hover:bg-[#021120EE] text-white cursor-pointer'
  }, [isCommentModeActive, isCheckLockedPoint, isLockedByAnother])

  return {
    isCheckLockedPoint,
    isLockedByAnother,
    isCommentModeActive,
    toggleCommentLock,
    buttonClassName
  }
}
